export interface BackendAvailabilityState {
  unavailable: boolean
  lastErrorMessage: string | null
  since: number | null
}

const BACKEND_UNAVAILABLE_FAILURE_THRESHOLD = 3
const BACKEND_UNAVAILABLE_FAILURE_WINDOW_MS = 15_000

type BackendUnavailableOptions = {
  immediate?: boolean
}

let backendAvailabilityState: BackendAvailabilityState = {
  unavailable: false,
  lastErrorMessage: null,
  since: null,
}
let backendAvailabilityFailureCount = 0
let backendAvailabilityFirstFailureAt: number | null = null
const backendAvailabilityListeners = new Set<(state: BackendAvailabilityState) => void>()

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

function extractHttpStatusFromError(error: Error): number | null {
  const candidateStatus = (error as Error & { status?: unknown }).status
  if (typeof candidateStatus === 'number' && Number.isFinite(candidateStatus)) {
    return Math.trunc(candidateStatus)
  }

  const statusMatch = error.message.match(/\bAPI error:\s*(\d{3})\b/i)
  if (!statusMatch) return null

  const parsed = Number.parseInt(statusMatch[1] ?? '', 10)
  return Number.isFinite(parsed) ? parsed : null
}

function emitBackendAvailabilityState() {
  backendAvailabilityListeners.forEach((listener) => listener(backendAvailabilityState))
}

function shouldIgnoreBackendAvailabilityError(error: Error) {
  if (error.name === 'AbortError') return true

  const status = extractHttpStatusFromError(error)
  if (status !== null && status >= 400 && status < 500) {
    return true
  }

  return false
}

function registerBackendFailure(now: number) {
  if (
    backendAvailabilityFirstFailureAt === null
    || now - backendAvailabilityFirstFailureAt > BACKEND_UNAVAILABLE_FAILURE_WINDOW_MS
  ) {
    backendAvailabilityFirstFailureAt = now
    backendAvailabilityFailureCount = 1
    return backendAvailabilityFailureCount
  }

  backendAvailabilityFailureCount += 1
  return backendAvailabilityFailureCount
}

function isNetworkError(error: Error): boolean {
  return error.name === 'TypeError'
    || error.message === 'Failed to fetch'
    || error.message === 'Load failed'
    || error.message.includes('NetworkError')
    || error.message.includes('ECONNREFUSED')
}

export function markBackendUnavailable(error?: unknown, options: BackendUnavailableOptions = {}) {
  const normalizedError = error ? normalizeError(error) : null
  if (normalizedError && shouldIgnoreBackendAvailabilityError(normalizedError)) {
    return
  }

  const now = Date.now()
  const failureCount = registerBackendFailure(now)
  const isNetwork = normalizedError ? isNetworkError(normalizedError) : false
  if (
    !options.immediate
    && !backendAvailabilityState.unavailable
    && !isNetwork
    && failureCount < BACKEND_UNAVAILABLE_FAILURE_THRESHOLD
  ) {
    return
  }

  const nextState: BackendAvailabilityState = {
    unavailable: true,
    lastErrorMessage: normalizedError?.message ?? backendAvailabilityState.lastErrorMessage ?? null,
    since: backendAvailabilityState.unavailable ? backendAvailabilityState.since : now,
  }

  if (
    backendAvailabilityState.unavailable === nextState.unavailable
    && backendAvailabilityState.lastErrorMessage === nextState.lastErrorMessage
    && backendAvailabilityState.since === nextState.since
  ) {
    return
  }

  backendAvailabilityState = nextState
  emitBackendAvailabilityState()
}

export function markBackendAvailable() {
  backendAvailabilityFailureCount = 0
  backendAvailabilityFirstFailureAt = null

  if (!backendAvailabilityState.unavailable && backendAvailabilityState.lastErrorMessage === null) {
    return
  }

  backendAvailabilityState = {
    unavailable: false,
    lastErrorMessage: null,
    since: null,
  }
  emitBackendAvailabilityState()
}

export function getBackendAvailabilityState(): BackendAvailabilityState {
  return backendAvailabilityState
}

export function subscribeBackendAvailability(
  listener: (state: BackendAvailabilityState) => void
): () => void {
  backendAvailabilityListeners.add(listener)
  return () => {
    backendAvailabilityListeners.delete(listener)
  }
}
